import { useState } from "react";
import { useEffect, useRef } from "react";
import { X, Megaphone, ArrowUpRight } from "lucide-react";
import { useLang } from "../../lib/i18n";

const keyFor = (a) => `mrva-ann-${a.text}|${a.text_ar}`;

export const isAnnouncementLive = (a) => !!(a?.enabled && (a.text || a.text_ar));

export const AnnouncementBar = ({ data, onDismiss, onHeight }) => {
  const { pick } = useLang();
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => onHeight(el.offsetHeight));
    ro.observe(el);
    return () => { ro.disconnect(); onHeight(0); };
  }, [onHeight]);
  const text = pick(data, "text");
  const label = pick(data, "link_label");
  const tone = data.color === "accent2" ? "bg-accent2 text-white" : data.color === "dark" ? "bg-white text-black" : "bg-accent text-onaccent";
  return (
    <div ref={ref} className={`relative flex min-h-10 items-center justify-center gap-3 px-12 py-2 text-center text-[13px] font-semibold sm:text-sm ${tone}`} role="status" data-testid="announcement-bar">
      <Megaphone size={15} className="hidden shrink-0 sm:block" />
      <span data-testid="announcement-text">{text}</span>
      {label && data.link_url && (
        <a href={data.link_url} target="_blank" rel="noreferrer" data-testid="announcement-link"
          className="inline-flex shrink-0 items-center gap-1 underline underline-offset-4 hover:no-underline">
          {label} <ArrowUpRight size={14} className="rtl:-scale-x-100" />
        </a>
      )}
      <button onClick={onDismiss} aria-label="Close announcement" data-testid="announcement-close-btn"
        className="absolute end-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full opacity-70 transition-opacity hover:opacity-100">
        <X size={16} />
      </button>
    </div>
  );
};

export const useAnnouncement = (data) => {
  const [dismissed, setDismissed] = useState(() => new Set());
  const key = keyFor(data || {});
  const hidden = dismissed.has(key) || sessionStorage.getItem(key) === "1";
  const show = isAnnouncementLive(data) && !hidden;
  const dismiss = () => {
    sessionStorage.setItem(key, "1");
    setDismissed((s) => new Set(s).add(key));
  };
  return { show, dismiss };
};
